import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import {
  initialWindowMode,
  type MobileAnchor,
  type WindowCorner,
  type WindowMode,
} from '#features/media-window/model';

/**
 * Как показана комната сессии: свёрнутая пилюля или плавающее окно.
 *
 * Третьего значения `WindowMode` – `full` – здесь нет намеренно: «весь экран» у комнаты это
 * маршрут треда `/dashboard/chats/$chatId`, а не ещё одно состояние коробки. Хранить его в сторе
 * значило бы завести второй источник правды о том, где человек сейчас находится, – им уже
 * является адрес страницы.
 */
export type SessionRoomMode = Exclude<WindowMode, 'full'>;

type SessionRoomSlice = {
  mode: SessionRoomMode;
  /** Сессия, чей виджет действительно виден на экране. Не сохраняется между загрузками. */
  visibleSessionId: string | null;
  /** Угол экрана на десктопе – коробку перетаскивают, и она прилипает к ближайшему. */
  corner: WindowCorner;
  /** Край экрана на телефоне: внизу полоса накрывает поле ввода, наверху – освобождает его. */
  /**
   * Край комнаты на экране переписки (02.10.2026). Там по умолчанию верх – под шапкой, подальше от
   * поля ввода, – но человек может унести комнату вниз, и этот выбор живёт отдельно от общего:
   * в переписке и в ленте удобный край разный.
   */
  chatAnchor: MobileAnchor;
  mobileAnchor: MobileAnchor;
  /**
   * Режим, из которого ушли на весь экран, чтобы вернуться ровно в него.
   *
   * Без этой памяти уход в тред и возврат назад всегда открывали окно – в том числе тому, кто
   * держал комнату свёрнутой пилюлей и просил её не мешать.
   */
  modeBeforeFull: SessionRoomMode | null;
  /** Раскрыт ли аккордеон состава внутри комнаты. */
  voiceListOpen: boolean;
  /**
   * Сессия, чью плашку «Завершена» уже закрыли руками.
   *
   * Держим id, а не флаг: флаг погасил бы и плашку следующей сессии, которая начнётся через час
   * по той же заявке. Переживает перезагрузку – закрытое человеком не возвращается по F5.
   */
  dismissedEndedFor: string | null;
  /**
   * Сессии, чьё первое появление комната уже отыграла: окно на десктопе, шторка на телефоне.
   *
   * Переживает перезагрузку – F5 не открывает окно заново тому, кто его уже свернул. Потолок
   * {@link ANNOUNCED_LIMIT}: старые вытесняются, сессия дважды не повторяется. `null` – память ещё
   * не заведена (первый запуск после выката или новый браузер): тогда сессия, появившаяся сама,
   * отмечается молча, а не распахивает окно всем, кто держал комнату свёрнутой.
   */
  announcedSessionIds: string[] | null;
  /**
   * «Я только что вступил в эту сессию» – жест этой вкладки, а не состояние. Не сохраняется: после
   * перезагрузки жеста уже нет. По нему комната после вступления разворачивается сама, а на
   * телефоне один раз открывает шторку (бриф приглашений §6.1).
   */
  expectedJoin: { sessionId: string; at: number } | null;
  expectedLeave: { sessionId: string; at: number } | null;

  setMode: (mode: SessionRoomMode) => void;
  setVisibleSessionId: (sessionId: string | null) => void;
  setCorner: (corner: WindowCorner) => void;
  setMobileAnchor: (anchor: MobileAnchor) => void;
  setChatAnchor: (anchor: MobileAnchor) => void;
  setVoiceListOpen: (open: boolean) => void;
  /** Уходим на весь экран (в тред): запоминаем, во что схлопнуться на обратном пути. */
  enterFull: () => void;
  /** Вернулись с треда: восстанавливаем прежний режим, а не открываем окно по умолчанию. */
  exitFull: () => void;
  dismissEnded: (sessionId: string) => void;
  isEndedDismissed: (sessionId: string | null | undefined) => boolean;
  /** Ставят все обработчики вступления и создания сессии в этой вкладке. */
  expectJoin: (sessionId: string) => void;
  /**
   * «Я ухожу из этой сессии сам»: ставят выход из состава и отмена сессии автором.
   *
   * Нужна, чтобы отличить свой уход от конца сессии. Из ответа сервера они выглядят одинаково -
   * строка сессии просто пропадает, - и без отметки комната либо молчала бы о завершении хостом,
   * либо говорила «Сессия закончилась» тому, кто сам нажал «Выйти».
   */
  expectLeave: (sessionId: string) => void;
  /** Снять ошибочную отметку, если сервер отказал в выходе. */
  clearExpectedLeave: (sessionId: string) => void;
  /** Одноразово: `true`, если человек уходил сам и недавно. Снимает отметку. */
  takeExpectedLeave: (sessionId: string) => boolean;
  /** Одноразово: `true`, если жест был и не старше {@link EXPECTED_JOIN_TTL_MS}. Снимает отметку. */
  takeExpectedJoin: (sessionId: string) => boolean;
  markAnnounced: (sessionId: string) => void;
};

/** Сколько первых появлений помним. Больше двадцати сессий между перезагрузками не бывает. */
export const ANNOUNCED_LIMIT = 20;
/** Жест вступления живёт полминуты: сессия приезжает рефетчем за секунды, чат – до 16 с. */
export const EXPECTED_JOIN_TTL_MS = 30_000;

/**
 * С чего комната начинается на этом экране: на телефоне – полоса, на десктопе – окно. Правило
 * общее с окном разговора, поэтому берём его оттуда, а не заводим второе.
 */
const initialMode = (): SessionRoomMode => (initialWindowMode() === 'mini' ? 'mini' : 'window');

/**
 * Поверхность комнаты сессии: режим, угол, край, память о полноэкранном и о закрытой плашке.
 *
 * Живёт отдельно от `voiceStore` не ради чистоты слоёв, а потому что комната переживает выход из
 * голоса: там `setConnecting` сбрасывает режим окна каждым входом, а `reset()` гасит состояние
 * целиком – и комната, которой голос вообще не нужен (сессия идёт, в голос я не заходил),
 * теряла бы выбранный человеком угол и размер на ровном месте.
 */
export const useSessionRoomStore = create<SessionRoomSlice>()(
  persist(
    (set, get) => ({
      mode: initialMode(),
      visibleSessionId: null,
      corner: 'br',
      mobileAnchor: 'bottom',
      chatAnchor: 'top',
      modeBeforeFull: null,
      voiceListOpen: false,
      dismissedEndedFor: null,
      announcedSessionIds: null,
      expectedJoin: null,
      expectedLeave: null,

      setMode: (mode) => set({ mode }),
      setVisibleSessionId: (visibleSessionId) => set({ visibleSessionId }),
      setCorner: (corner) => set({ corner }),
      setMobileAnchor: (mobileAnchor) => set({ mobileAnchor }),
      setChatAnchor: (chatAnchor) => set({ chatAnchor }),
      setVoiceListOpen: (voiceListOpen) => set({ voiceListOpen }),

      enterFull: () => set({ modeBeforeFull: get().mode }),
      exitFull: () =>
        set((state) => ({
          mode: state.modeBeforeFull ?? state.mode,
          modeBeforeFull: null,
        })),

      dismissEnded: (sessionId) => set({ dismissedEndedFor: sessionId }),
      isEndedDismissed: (sessionId) => Boolean(sessionId) && get().dismissedEndedFor === sessionId,

      expectJoin: (sessionId) => set({ expectedJoin: { sessionId, at: Date.now() } }),

      expectLeave: (sessionId) => set({ expectedLeave: { sessionId, at: Date.now() } }),
      clearExpectedLeave: (sessionId) =>
        set((state) =>
          state.expectedLeave?.sessionId === sessionId ? { expectedLeave: null } : state
        ),
      takeExpectedLeave: (sessionId) => {
        const expected = get().expectedLeave;
        if (!expected || expected.sessionId !== sessionId) {
          return false;
        }
        set({ expectedLeave: null });

        return Date.now() - expected.at <= EXPECTED_JOIN_TTL_MS;
      },
      takeExpectedJoin: (sessionId) => {
        const expected = get().expectedJoin;
        if (!expected) {
          return false;
        }
        const fresh = Date.now() - expected.at <= EXPECTED_JOIN_TTL_MS;
        if (expected.sessionId === sessionId || !fresh) {
          // Своя отметка тратится, чужая – только когда протухла: жест мог относиться к сессии,
          // которая приедет следующим рефетчем.
          set({ expectedJoin: null });
        }
        return fresh && expected.sessionId === sessionId;
      },
      markAnnounced: (sessionId) =>
        set((state) => {
          const current = state.announcedSessionIds ?? [];
          if (current.includes(sessionId)) {
            return state;
          }
          return { announcedSessionIds: [...current, sessionId].slice(-ANNOUNCED_LIMIT) };
        }),
    }),
    {
      name: 'session-room',
      storage: createJSONStorage(() => localStorage),
      /*
       * Функции в хранилище не кладём: `persist` пишет весь снимок, а после разбора JSON на их
       * месте оказались бы `undefined` – и первый же вызов действия из восстановленного стора
       * уронил бы комнату.
       */
      partialize: (state) => ({
        mode: state.mode,
        corner: state.corner,
        mobileAnchor: state.mobileAnchor,
        chatAnchor: state.chatAnchor,
        modeBeforeFull: state.modeBeforeFull,
        voiceListOpen: state.voiceListOpen,
        dismissedEndedFor: state.dismissedEndedFor,
        announcedSessionIds: state.announcedSessionIds,
      }),
    }
  )
);
